import { Suspense, use, useEffect, useState } from 'react';
import { useFormState } from 'react-dom';
import { datadogLogs } from '@datadog/browser-logs';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import { useRouter } from 'next/navigation';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { analytics } from '@/analytics/segment';
import { useGhostStore } from '@/legacy/store';
import { FilterAttribute } from '@/types/filterAttribute';
import { FormState, savePartialPreferences } from '../../preferences/actions';
import { BrandsStep } from './BrandsStep';
import { CategoriesStep } from './CategoriesStep';
import { OtherValues } from './OtherValues';
import { PercentOffMsrpStep } from './PercentOffMsrpStep';
import { PlatformsStep } from './PlatformsStep';
import { SpecialtiesStep } from './SpecialtiesStep';
import { StepContext } from './StepContext';
import { STEPS } from './types';

const getSelection = (filterAttribute: FilterAttribute, payload: FormData) => {
  switch (filterAttribute) {
    case 'percentOffMsrp':
      return {
        platforms: payload.get('percentOffMsrpClassificationId'),
      };
    case 'platforms':
      return {
        platforms: payload.getAll('platforms[]'),
      };
    case 'compositeCategories':
      return {
        platforms: payload.getAll('categories[]'),
      };
    case 'specialties':
      return {
        platforms: payload.getAll('specialties[]'),
      };
    case 'brands':
      return {
        platforms: payload.getAll('brands[]'),
      };
    default:
      return null;
  }
};

export default function Wizard({
  isDesktop,
  categoriesPromise,
  preferencesFormPromise,
  handleClose,
}: {
  isDesktop: boolean;
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  preferencesFormPromise: Promise<FormState>;
  handleClose: () => void;
}): JSX.Element {
  const initialState = use(preferencesFormPromise);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [state, formAction] = useFormState(savePartialPreferences, initialState);
  const router = useRouter();
  const stepContext = use(StepContext);
  const { setBuyerPreferences } = useGhostStore((state) => state);

  useEffect(() => {
    if (state?.valid && state.saved) {
      setBuyerPreferences(state);
    }
    if (!state?.valid) {
      datadogLogs.logger.error(
        'Unable to save form preferences: form not valid when it is always expected to be valid.',
      );
    }
  }, [state, setBuyerPreferences]);

  if (!initialState.valid || !state) return <></>;

  const handleFormAction = (payload: FormData) => {
    formAction(payload);
    router.refresh(); // updates the home page after each step

    analytics.track(AnalyticsEventNames.BUYER_PREFERENCES_MODAL_NEXT, {
      stepIndex: stepIndex,
      stepName: STEPS[stepIndex].filterAttribute,
      madeChanges: stepContext.isDirty,
      values: getSelection(STEPS[stepIndex].filterAttribute, payload),
    });

    if (stepIndex < STEPS.length - 1) {
      const newStepIndex = stepIndex + 1;
      setStepIndex(newStepIndex);
      stepContext.setStepIndex(newStepIndex);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    analytics.track(AnalyticsEventNames.BUYER_PREFERENCES_MODAL_BACK, {
      stepIndex: stepIndex,
      stepName: STEPS[stepIndex].filterAttribute,
      madeChanges: stepContext.isDirty,
    });

    setStepIndex((prev) => {
      const newIndex = prev > 0 ? prev - 1 : prev;
      stepContext.setStepIndex(newIndex);
      return newIndex;
    });
  };

  return (
    <>
      <form action={handleFormAction}>
        <DialogContent>
          <Box p={0.25}>
            {STEPS[stepIndex].filterAttribute === 'percentOffMsrp' && (
              <PercentOffMsrpStep initialValue={initialState.percentOffMsrpClassificationId} />
            )}
            {STEPS[stepIndex].filterAttribute === 'platforms' && (
              <PlatformsStep initialValues={initialState.platforms || []} />
            )}
            {STEPS[stepIndex].filterAttribute === 'compositeCategories' && (
              <Suspense>
                <CategoriesStep
                  categoriesPromise={categoriesPromise}
                  initialValues={initialState.compositeCategories || []}
                />
              </Suspense>
            )}
            {STEPS[stepIndex].filterAttribute === 'specialties' && (
              <SpecialtiesStep initialValues={initialState.specialties || []} />
            )}
            {STEPS[stepIndex].filterAttribute === 'brands' && (
              <BrandsStep initialValues={initialState.brands || []} />
            )}
            <OtherValues
              currentState={state.saved ? state : initialState}
              omit={STEPS[stepIndex].filterAttribute}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            marginTop: 4,
            justifyContent: isDesktop ? 'flex-end' : 'center',
          }}
        >
          <Stack
            gap={{ mobile: 2, desktop: 1 }}
            flexGrow={{ mobile: 1, desktop: 0 }}
            direction={{ mobile: 'column', desktop: 'row' }}
          >
            <Button
              onClick={handleBack}
              variant="outlined"
              fullWidth={!isDesktop}
              disabled={stepIndex === 0}
            >
              Back
            </Button>
            <Button type="submit" fullWidth={!isDesktop}>
              {stepIndex === STEPS.length - 1 ? 'Save' : 'Next'}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </>
  );
}
